import React from "react"

import { Link, useHistory } from "react-router-dom"

import { ChatBubbleLeftIcon } from "@heroicons/react/24/outline"

import classNames from "src/classNames"

import LocalTime from "components/LocalTime"
import Avatar from "components/Messaging/Avatar"

type User = {
  id: string
  type: "Teacher" | "Tutor" | "Student" | "Parent"
  messagingDisplayName: string
  avatar: {
    thumbUrl: string
  }
}
export type Thread = {
  id: string
  title: string
  messagesCount: number
  users: User[]
  lastMessage?: {
    id: string
    read: boolean
    content: string
    createdAt: string
    author: User
  }
}

export type ListProps = {
  viewerId: string
  threads: Thread[]
}

const ThreadsList: React.FC<ListProps> = ({ viewerId, threads }) => {
  const history = useHistory()

  return (
    <ul className="mt-2">
      {threads.map(thread => (
        <li
          role="presentation"
          onClick={() => history.push(`/messages/${thread.id}`)}
          key={thread.id}
          className={classNames(
            "flex cursor-pointer flex-wrap items-center justify-between gap-x-6 gap-y-4 border-0 border-b-2 border-solid border-b-slate-300/50 py-5 px-2 sm:flex-nowrap",
            !thread.lastMessage?.read && "italic"
          )}
        >
          <div>
            <p
              className={classNames(
                "text-sm/6 text-gray-900",
                !thread.lastMessage?.read ? "font-bold" : "font-medium"
              )}
            >
              <Link to={`/messages/${thread.id}`} className="hover:underline">
                {thread.title}
              </Link>
            </p>

            {thread.lastMessage && (
              <>
                <div
                  className={classNames(
                    !thread.lastMessage.read && "font-bold"
                  )}
                >
                  {thread.lastMessage.content}
                </div>
                <div className="text-xs/5 mt-1 flex items-center gap-x-2 text-gray-500">
                  <>
                    <p>
                      {thread.lastMessage.author.id === viewerId
                        ? "You"
                        : thread.lastMessage.author.messagingDisplayName}
                    </p>
                    <p>
                      <LocalTime
                        timestamp={thread.lastMessage.createdAt}
                        relative
                      />
                    </p>
                  </>
                </div>
              </>
            )}
          </div>

          <dl className="flex w-full flex-none justify-between gap-x-8 sm:w-auto">
            <div className="flex space-x-2 bg-transparent">
              <dt className="sr-only">Members</dt>
              {thread.users.map(user => (
                <dd key={user.id}>
                  <Avatar
                    alt={user.messagingDisplayName}
                    src={user.avatar.thumbUrl}
                  />
                </dd>
              ))}
            </div>
            <div className="flex h-16 gap-x-2.5">
              <dt className="">
                <span className="sr-only">Total messages</span>
                <ChatBubbleLeftIcon
                  aria-hidden="true"
                  className={classNames(
                    "h-8",
                    !thread.lastMessage?.read ? "text-red-600" : "text-gray-400"
                  )}
                />
              </dt>
              <dd className="text-sm/6 m-0 text-gray-900">
                {thread.messagesCount}
              </dd>
            </div>
          </dl>
        </li>
      ))}
    </ul>
  )
}

export default ThreadsList
